import { Badge } from "@/components/ui/badge";
import { Sparkles, AlertTriangle, ShieldAlert, Info, CheckCircle2, Quote } from "lucide-react";

const SEV = {
  critical: { c: "bg-red-100 text-red-800 border-red-200", i: ShieldAlert },
  major: { c: "bg-orange-100 text-orange-800 border-orange-200", i: AlertTriangle },
  moderate: { c: "bg-amber-100 text-amber-800 border-amber-200", i: AlertTriangle },
  minor: { c: "bg-slate-100 text-slate-700 border-slate-200", i: Info },
};

const REC = {
  approve: "text-emerald-700 bg-emerald-50 border-emerald-200",
  revise_and_resubmit: "text-amber-700 bg-amber-50 border-amber-200",
  decline: "text-red-700 bg-red-50 border-red-200",
};

export default function AiReport({ report, error }) {
  if (error)
    return (
      <div className="rounded-md border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800" data-testid="ai-error">
        AI diagnostic could not be generated ({String(error).slice(0, 120)}). A human reviewer can still proceed manually.
      </div>
    );
  if (!report)
    return (
      <div className="rounded-md border border-border bg-muted/40 p-4 text-sm text-muted-foreground" data-testid="ai-pending">
        AI diagnostic is being prepared…
      </div>
    );

  const issues = report.issues || [];
  return (
    <div className="space-y-4" data-testid="ai-report">
      <div className="rounded-lg border border-border bg-gradient-to-br from-secondary/60 to-transparent p-4">
        <div className="flex items-center gap-2 mb-2">
          <Sparkles className="h-4 w-4 brand-text" />
          <span className="font-heading font-semibold text-sm">AI Supervision Assistant — Advisory Diagnostic</span>
        </div>
        <p className="text-sm text-foreground/80">{report.summary}</p>
        {report.advisory_recommendation && (
          <div className={`mt-3 inline-flex flex-col gap-1 rounded-md border px-3 py-2 text-xs ${REC[report.advisory_recommendation] || ""}`}>
            <span className="font-semibold uppercase tracking-wide">
              Advisory: {report.advisory_recommendation.replaceAll("_", " ")}
            </span>
            {report.recommendation_rationale && <span className="opacity-80">{report.recommendation_rationale}</span>}
          </div>
        )}
        <p className="mt-3 text-[11px] text-muted-foreground italic">
          Advisory only — the human reviewer makes all binding decisions.
        </p>
      </div>

      {report.integrity_signals?.length > 0 && (
        <div className="rounded-lg border-2 border-red-300 bg-red-50 p-4" data-testid="integrity-panel">
          <div className="flex items-center gap-2 mb-1">
            <ShieldAlert className="h-4 w-4 text-red-600" />
            <span className="font-heading font-semibold text-sm text-red-800">
              Integrity Signals — {report.integrity_signals.length} flag{report.integrity_signals.length > 1 ? "s" : ""} for human review
            </span>
          </div>
          <p className="text-xs text-red-700/80 mb-3">Advisory indicators of possibly copied passages or fabricated references. These are NOT proof of misconduct — a human reviewer must verify.</p>
          <div className="space-y-2">
            {report.integrity_signals.map((sig, i) => (
              <div key={i} className="rounded-md bg-white border border-red-200 p-3" data-testid={`integrity-${i}`}>
                <div className="flex items-center justify-between gap-2">
                  <span className="text-[11px] font-semibold uppercase text-red-700">{(sig.type || "").replaceAll("_", " ")}</span>
                  <span className={`text-[10px] px-2 py-0.5 rounded-full uppercase font-medium ${sig.confidence === "high" ? "bg-red-600 text-white" : sig.confidence === "medium" ? "bg-orange-200 text-orange-800" : "bg-slate-200 text-slate-700"}`}>{sig.confidence} confidence</span>
                </div>
                {sig.evidence && (
                  <div className="mt-2 flex gap-2 rounded-md bg-red-50 p-2 text-xs text-foreground/70">
                    <Quote className="h-3 w-3 mt-0.5 shrink-0 text-red-500" />
                    <span className="italic">{sig.evidence}</span>
                  </div>
                )}
                {sig.explanation && <p className="mt-1 text-xs text-foreground/70">{sig.explanation}</p>}
              </div>
            ))}
          </div>
        </div>
      )}

      {report.checks && (
        <div className="flex flex-wrap gap-2">
          {Object.entries(report.checks).map(([k, v]) => {
            const goodWhenTrue = !k.toLowerCase().includes("flag");
            const ok = goodWhenTrue ? v : !v;
            return (
              <Badge key={k} variant="outline" className="gap-1 font-normal">
                {ok ? <CheckCircle2 className="h-3 w-3 text-emerald-600" /> : <AlertTriangle className="h-3 w-3 text-amber-600" />}
                {k.replaceAll("_", " ")}: {ok ? "OK" : "Review"}
              </Badge>
            );
          })}
        </div>
      )}

      <div className="space-y-3" data-testid="ai-issues">
        {issues.map((it, i) => {
          const sev = SEV[it.severity] || SEV.minor;
          const SIcon = sev.i;
          return (
            <div key={i} className="rounded-lg border border-border bg-card p-4" data-testid={`ai-issue-${i}`}>
              <div className="flex items-center justify-between gap-2 mb-2">
                <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-medium uppercase ${sev.c}`}>
                  <SIcon className="h-3 w-3" /> {it.severity}
                </span>
                <span className="text-[11px] text-muted-foreground">{it.criterion}</span>
              </div>
              <p className="text-sm font-medium text-foreground">{it.finding}</p>
              {it.evidence && (
                <div className="mt-2 flex gap-2 rounded-md bg-secondary/60 p-2 text-xs text-foreground/70">
                  <Quote className="h-3 w-3 mt-0.5 shrink-0 brand-text" />
                  <span className="italic">{it.evidence}</span>
                </div>
              )}
              {it.suggestion && <p className="mt-2 text-xs text-foreground/70"><b>Suggestion:</b> {it.suggestion}</p>}
              {it.policy_basis && <p className="mt-1 text-[11px] text-muted-foreground"><b>Policy basis:</b> {it.policy_basis}</p>}
            </div>
          );
        })}
      </div>
    </div>
  );
}
